import i18n from 'services/i18n';
import {
  AMOUNT_DEFAULT_VALUE,
  REUSABLE_FORM_FIELDS,
  REUSABLE_FORM_INITIAL_VALUES as initialValues
} from 'consts';
import { REUSABLE_LINK } from 'routes/paths';
import { formatNumberToStringByLocale } from 'utilities/number';
import { getRouteWithToken } from 'utilities/routes';

interface IFormInitialValues {
  pageData: Record<string, any>;
  savedValues: Record<string, any>;
}

interface IAmountRangeErrorMessage {
  minAmount: number;
  maxAmount: number;
  currency: string;
  parsedValue: number;
}

const getInitialAmount = (pageData: Record<string, any>) => {
  const { amount, amount_is_editable: amountIsEditable } = pageData || {};
  if (amountIsEditable && !amount) return AMOUNT_DEFAULT_VALUE;
  return amount || AMOUNT_DEFAULT_VALUE;
};

const getInitialCurrency = (pageData: Record<string, any>) => {
  const { currency } = pageData || {};
  return currency;
};

const getInitialValueByKey = {
  default: () => '',
  [REUSABLE_FORM_FIELDS.amount]: getInitialAmount,
  [REUSABLE_FORM_FIELDS.currency]: getInitialCurrency
};

export const getReusableFormInitialValues = ({
  pageData,
  savedValues
}: IFormInitialValues) => {
  const initial = Object.keys(initialValues).reduce((acc, key) => {
    const getInitialValueFunc = () =>
      (getInitialValueByKey[key] || getInitialValueByKey.default)(pageData);
    return {
      ...acc,
      [key]: savedValues?.[key] || getInitialValueFunc()
    };
  }, {});
  return initial;
};

const MAX_CHARS_DESKTOP = 12;
const MAX_CHARS_MOBILE = 11;

export const checkIfValueValid = (values: Record<string, any>, isMobile: boolean) => {
  const maxCharsAllowed = isMobile ? MAX_CHARS_MOBILE : MAX_CHARS_DESKTOP;
  return values?.value?.length <= maxCharsAllowed;
};

export const getAmountRangeErrorMessage = ({
  minAmount,
  maxAmount,
  currency,
  parsedValue
}: IAmountRangeErrorMessage) => {
  if (
    (minAmount <= parsedValue && parsedValue <= maxAmount) ||
    parsedValue === AMOUNT_DEFAULT_VALUE
  ) return '';

  const addSeperatorMin =
    formatNumberToStringByLocale({ number: minAmount, locale: i18n.language }) ||
    minAmount;
  const addSeperatorMax =
    formatNumberToStringByLocale({ number: maxAmount, locale: i18n.language }) ||
    maxAmount;

  if (parsedValue >= maxAmount) {
    return {
      key: 'reusable.amount_exceeded_error_message',
      params: {
        addSeperatorMax,
        currency
      }
    };
  }
  return {
    key: 'reusable.amount_subceeded_error_message',
    params: {
      addSeperatorMin,
      currency
    }
  };
};

export const hasReachedMaxUsage = (maxUsageUnix: number) => {
  if (!maxUsageUnix) return false;
  return maxUsageUnix >= Math.floor(Date.now() / 1000);
};

export const calcReusablePageLink = (reusableLinkToken = '') => {
  const {
    location: { origin: baseUrl }
  } = window;
  const reusableV2Route = getRouteWithToken(REUSABLE_LINK, reusableLinkToken);
  const reusablePageLink = `${baseUrl}${reusableV2Route}`;

  return reusablePageLink;
};
